/**
 * Нижнее меню и главный экран.
 *
 * Раньше приложение сразу требовало завести выставку — человек попадал
 * в настройку вместо того, чтобы увидеть, где он находится. Теперь выставка
 * это состояние на главной, которое можно поменять, а не ворота на входе.
 */
import { useEffect, useMemo, useState } from 'react';
import { THEME_NAME, currentTheme, doubleTap, nextTheme, onThemeChange, toggleTheme, type Theme } from '../lib/theme';
import { db, type LocalExhibition, type LocalSupplier, type LocalStone } from '../db';
import { pictureUrl, releasePictures } from '../lib/pictures';
import { plural, formatPhone, placeTitle, type Filter } from '@core';
import { whereIs } from '../lib/where';
import { useUnlinkedTalks } from './Talks';
import { useTaskBadge } from './Tasks';
import { shortNo } from './Browse';
import { loadRows } from '../lib/catalog';
import { rowPerCt, rowWeight, rowCurrency, parseMoney, formatMoney, type StoneRow } from '@core';

export type Tab = 'today' | 'stones' | 'suppliers' | 'more';

/** Текущее оформление как состояние: строка в «Ещё» перерисовывается при смене. */
function useTheme(): Theme {
  const [t, setT] = useState<Theme>(currentTheme());
  useEffect(() => onThemeChange(setT), []);
  return t;
}

const TAB_ICON: Record<string, string> = {
  today: '<path d="M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/>',
  stones: '<path d="M12 3l2.2 5.8L20 11l-5.8 2.2L12 19l-2.2-5.8L4 11l5.8-2.2z"/>',
  suppliers: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M9 10v10"/>',
  more: '<circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/>',
};

export function TabBar({ tab, onTab, offices }: { tab: Tab; onTab: (t: Tab) => void; offices?: boolean }) {
  // В здании стендов нет — есть офисы. Подпись вкладки идёт за местом.
  const items: Array<{ id: Tab; cap: string }> = [
    { id: 'today', cap: 'Сегодня' },
    { id: 'stones', cap: 'Камни' },
    { id: 'suppliers', cap: offices ? 'Офисы' : 'Стенды' },
    { id: 'more', cap: 'Ещё' },
  ];
  return (
    <nav className="tabbar">
      {items.map((it) => (
        <button key={it.id} className="tab" aria-current={tab === it.id ? 'page' : undefined}
                onClick={() => onTab(it.id)}>
          <svg className="ico" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
               dangerouslySetInnerHTML={{ __html: TAB_ICON[it.id] }} />
          <span className="cap">{it.cap}</span>
        </button>
      ))}
    </nav>
  );
}

// ── главная ─────────────────────────────────────────────────────────────────

/**
 * Быстрые вопросы к каталогу.
 *
 * Это не украшение главного экрана, а те вопросы, которые на выставке задают
 * чаще всего: что отобрано, что ещё без цены, что с проверенным отчётом.
 * Каждый открывает каталог уже с этим отбором — и там его можно доуточнить.
 */
const QUICK: Array<{ cap: string; filter: Filter }> = [
  { cap: 'Приоритет', filter: { statuses: ['priority'] } },
  { cap: 'Кандидаты', filter: { statuses: ['strong', 'interesting'] } },
  { cap: 'С отчётом', filter: { cert: 'certified' } },
  { cap: 'Дешевле за карат', filter: { sort: 'perCt' } },
];

/** Цена в строке реестра: с символом валюты, без копеек. */
function ledgerMoney(n: number | null | undefined, currency = 'USD'): string {
  if (n == null) return '—';
  const m = parseMoney(Math.round(n), currency);
  return m ? formatMoney(m) : String(n);
}
const STAMP: Record<string, { cls: string; cap: string }> = {
  certified: { cls: 'src-certified', cap: 'серт' },
  assessed: { cls: 'src-assessed', cap: 'гемм' },
  claimed: { cls: 'src-claimed', cap: 'пост' },
  low: { cls: 'src-low', cap: '—' },
};
/** Кто назвал цену: штамп источника, как в отчёте. */
function priceStamp(r: StoneRow) {
  const v = r.values.get('asking_price_per_ct') ?? r.values.get('asking_price_per_ct_lot') ?? r.values.get('asking_price');
  return STAMP[v?.confidence ?? 'low'] ?? STAMP.low;
}
function ledgerName(r: StoneRow): string {
  return [
    r.values.get('stone_type')?.text ?? r.values.get('variety')?.text,
    r.values.get('shape')?.text ?? r.values.get('cut')?.text,
  ].filter(Boolean).join(', ') || (r.kind === 'parcel' ? 'Партия' : 'Камень');
}

export function Today({ exhibition, me, onPickShow, onScanCard, onOpenSupplier, onOpenStone, onBrowse, onShoot, onTalk, onUnlinkedTalks, onTasks }: {
  exhibition: LocalExhibition | null;
  me: { name?: string; workspace_name?: string } | null;
  onPickShow: () => void;
  onScanCard: () => void;
  onOpenSupplier: (id: string) => void;
  onOpenStone: (id: string) => void;
  onBrowse: (filter?: Filter) => void;
  /** Снимать камни у последнего стенда — без захода в его карточку. */
  onShoot?: (supplierId: string) => void;
  /** Беседа через переводчика — у стенда она нужна сразу, а не через «Ещё». */
  onTalk?: () => void;
  /** Беседы, которые ещё не привязаны к стенду. */
  onUnlinkedTalks?: () => void;
  onTasks?: () => void;
}) {
  const unlinked = useUnlinkedTalks();
  const taskBadge = useTaskBadge();
  const [suppliers, setSuppliers] = useState<LocalSupplier[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [, setTally] = useState({ stones: 0, picked: 0 });
  const [recent, setRecent] = useState<LocalStone[]>([]);
  const [rows, setRows] = useState<Map<string, StoneRow>>(new Map());
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [total, setTotal] = useState(0);
  const [today, setToday] = useState({ shot: 0, picked: 0 });
  const [revisit, setRevisit] = useState<LocalSupplier[]>([]);

  useEffect(() => {
    void (async () => {
      // Каталог показываем всегда: он не кончается вместе с выставкой,
      // и вернуться к снятому вчера нужно чаще, чем кажется.
      setTotal(await db.stones.count());

      // Итог дня: что снято и отобрано именно сегодня, а не за всё время.
      const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
      const shotToday = (await db.stones.toArray())
        .filter((st) => new Date(st.capturedAt) >= midnight);
      setToday({
        shot: shotToday.length,
        picked: shotToday.filter((st) => ['interesting', 'strong', 'priority'].includes(st.status)).length,
      });
      setRevisit((await db.suppliers.toArray()).filter((sp) => sp.followUp));

      const last = await db.stones.reverse().sortBy('capturedAt');
      const head = last.slice(0, 12);
      setRecent(head);
      setRows(new Map((await loadRows()).map((r) => [r.id, r])));
      const map: Record<string, string> = {};
      for (const st of head) {
        const m = await db.media.where('subjectId').equals(st.id)
          .filter((x) => x.role === 'preview' || x.role === 'photo').first();
        const u = pictureUrl(m);
        if (u) map[st.id] = u;
      }
      setThumbs(map);

      if (!exhibition) { setSuppliers([]); setTally({ stones: 0, picked: 0 }); return; }
      setSuppliers(await db.suppliers.where('exhibitionId').equals(exhibition.id).reverse().sortBy('createdAt'));
      const stones = await db.stones.where('exhibitionId').equals(exhibition.id).toArray();
      const c: Record<string, number> = {};
      for (const s of stones) c[s.supplierId] = (c[s.supplierId] ?? 0) + 1;
      setCounts(c);
      setTally({
        stones: stones.length,
        picked: stones.filter((s) => s.status !== 'viewed' && s.status !== 'rejected').length,
      });
    })();
  }, [exhibition?.id]);

  // День считается только у выставки: у здания дней нет, туда приходят когда угодно.
  const building = exhibition?.kind === 'building';
  const flipTheme = useMemo(() => doubleTap(() => void toggleTheme()), []);
  const day = exhibition?.startsOn && !building
    ? Math.max(1, Math.floor((Date.now() - new Date(exhibition.startsOn).getTime()) / 86_400_000) + 1)
    : null;

  return (
    <div className="app tabbed">
      <div className="bar">
        {/* Строка места вместо карточки: где вы и какой день. Нажатие — сменить. */}
        {exhibition ? (
          <button className="hello place" onClick={onPickShow}>
            <span>{placeTitle(exhibition) || exhibition.code}</span>
            <span>{day ? `день ${day} · ` : ''}{new Date().toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}</span>
          </button>
        ) : (
          me?.name && <p className="hello">{me.name}{me.workspace_name ? ` · ${me.workspace_name}` : ''}</p>
        )}
        <div className="titlerow">
          {/* Двойное касание по заголовку меняет оформление: Лаборатория ⇄ Прибор. */}
          <h1 className="t" onClick={flipTheme}>Сегодня</h1>
          {onTalk && (
            <button className="btn btn-secondary btn-square talk-open" onClick={onTalk}
                    aria-label="Разговор с переводом" title="Разговор с переводом">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 4h10a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H9l-3 3v-3H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
                <path d="M19 8h1a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-1v3l-3-3h-4a2 2 0 0 1-2-2v-1"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="scroll">
        {!exhibition && (
          <button className="showcard empty" onClick={onPickShow}>
            <span className="lbl">Место не выбрано</span>
            <span className="nm">Выставка или здание</span>
            <span className="sub">Подставится во всё, что вы снимете</span>
          </button>
        )}

        {/* Сводка дня сразу под местом: три показания, как на панели прибора
            или в шапке отчёта. Ноль тоже показание — его не прячем. */}
        {(exhibition || total > 0) && (
          <div className="tally stats">
            <div className={today.shot ? 'on' : ''}><b>{today.shot}</b><span>снято</span></div>
            <div className={today.picked ? 'on' : ''}><b>{today.picked}</b><span>отобрано</span></div>
            <div className={suppliers.length ? 'on' : ''}><b>{suppliers.length}</b><span>{plural(suppliers.length, 'стенд', 'стенда', 'стендов')}</span></div>
          </div>
        )}

        {!exhibition && total === 0 && (
          <div className="blankslate">
            <b>С чего начинается день</b>
            Заведите выставку или здание — на это уйдёт полминуты. Дальше всё
            просто: снимаете визитку поставщика, и сразу открывается камера для камней.
          </div>
        )}

        {/* Задачи на сегодня и предложенные из бесед — пока их не разберут. */}
        {taskBadge > 0 && onTasks && (
          <div className="list" style={{ marginTop: 14 }}>
            <button className="row" onClick={onTasks}>
              <span className="grow">
                <span className="nm">Задачи</span>
                <span className="sub">{taskBadge} {plural(taskBadge, 'ждёт', 'ждут', 'ждут')} внимания: на сегодня и предложенные из бесед</span>
              </span>
              <span className="chev">›</span>
            </button>
          </div>
        )}

        {/* Беседа началась раньше, чем появился стенд, — напоминаем её привязать. */}
        {unlinked > 0 && onUnlinkedTalks && (
          <div className="list" style={{ marginTop: 14 }}>
            <button className="row" onClick={onUnlinkedTalks}>
              <span className="grow">
                <span className="nm">Беседы без стенда</span>
                <span className="sub">{unlinked} {plural(unlinked, 'беседа ждёт', 'беседы ждут', 'бесед ждут')} привязки к стенду</span>
              </span>
              <span className="chev">›</span>
            </button>
          </div>
        )}

        {revisit.length > 0 && (
          <>
            <div className="head">
              <h3>Вернуться сегодня</h3><span>{revisit.length}</span>
            </div>
            <div className="list">
              {revisit.map((sp) => (
                <button key={sp.id} className="row" onClick={() => onOpenSupplier(sp.id)}>
                  <span className="grow">
                    <span className="nm">{sp.companyName}</span>
                    <span className="sub">{whereIs(sp)}</span>
                  </span>
                  <span className="chev">›</span>
                </button>
              ))}
            </div>
          </>
        )}

        {recent.length > 0 && (
          <>
            <div className="head">
              <h3>{today.shot ? 'Снято сегодня' : 'Последние снятые'}</h3>
              <button className="linky" onClick={() => onBrowse(undefined)}>все {total} ›</button>
            </div>
            {/* Реестр, а не витрина: в строке видно вес, цену и кто её назвал —
                то, ради чего вечером открывают список. */}
            <div className="list ledger">
              {recent.slice(0, today.shot ? Math.max(today.shot, 5) : 6).map((st) => {
                const r = rows.get(st.id);
                const perCt = r ? rowPerCt(r) : null;
                const w = r ? rowWeight(r) : null;
                const stamp = r ? priceStamp(r) : STAMP.low;
                return (
                  <button key={st.id} className="row" onClick={() => onOpenStone(st.id)}>
                    <span className="mark">
                      {thumbs[st.id] ? <img src={thumbs[st.id]} alt="" /> : (st.kind === 'parcel' ? '⁘' : '◆')}
                    </span>
                    <span className="grow">
                      <span className="nm">{r ? ledgerName(r) : (st.kind === 'parcel' ? 'Партия' : 'Камень')}</span>
                      <span className="sub code">{shortNo(st.publicNo ?? st.localNo)}{r?.supplierName ? ` · ${r.supplierName}` : ''}</span>
                    </span>
                    <span className="num">
                      <b>{perCt != null ? `${ledgerMoney(perCt, r ? rowCurrency(r) : 'USD')}/ct` : '—'}</b>
                      <i>{w != null ? `${w} ct` : ''} <span className={`src ${stamp.cls}`}>{stamp.cap}</span></i>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {exhibition && suppliers.length === 0 && (
          <div className="blankslate">
            <b>Начните с визитки</b>
            Сфотографируйте карточку поставщика — данные заполнятся сами,
            и сразу откроется камера для камней.
          </div>
        )}
      </div>

      {/* Мёртвая серая кнопка без объяснения — худшее, что может встретить
          человека на первом экране. Пока выставки нет, действие одно, и оно
          ведёт туда, где её заводят. */}
      <div className="dock">
        {exhibition ? (
          <>
            <button className="btn btn-primary" onClick={onScanCard}>Снять визитку</button>
            {onShoot && suppliers[0] && (
              <button className="btn btn-secondary btn-square" aria-label={`Снимать камни: ${suppliers[0].companyName}`}
                      onClick={() => onShoot(suppliers[0].id)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.5"/>
                </svg>
              </button>
            )}
          </>
        ) : (
          <button className="btn btn-primary" onClick={onPickShow}>Выбрать место</button>
        )}
      </div>
    </div>
  );
}

// ── все поставщики ──────────────────────────────────────────────────────────

export function Suppliers({ onOpenSupplier, offices }: { onOpenSupplier: (id: string) => void; offices?: boolean }) {
  const [list, setList] = useState<LocalSupplier[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [shows, setShows] = useState<Record<string, string>>({});
  const [q, setQ] = useState('');

  useEffect(() => {
    void (async () => {
      const sup = await db.suppliers.reverse().sortBy('createdAt');
      setList(sup);
      const stones = await db.stones.toArray();
      const c: Record<string, number> = {};
      for (const s of stones) c[s.supplierId] = (c[s.supplierId] ?? 0) + 1;
      setCounts(c);
      const ex = await db.exhibitions.toArray();
      setShows(Object.fromEntries(ex.map((e) => [e.id, placeTitle(e) || e.code])));
    })();
  }, []);

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? list.filter((s) =>
        s.companyName.toLowerCase().includes(needle) ||
        (s.companyNameLocal ?? '').toLowerCase().includes(needle) ||
        (s.booth ?? '').toLowerCase().includes(needle) ||
        (s.office ?? '').toLowerCase().includes(needle) ||
        (s.city ?? '').toLowerCase().includes(needle))
    : list;

  return (
    <div className="app tabbed">
      <div className="bar">
        <h1 className="t">{offices ? 'Офисы и стенды' : 'Стенды'}</h1>
        <p className="meta">{list.length} {plural(list.length, 'поставщик', 'поставщика', 'поставщиков')} за всё время</p>
      </div>

      <div className="scroll">
        <label className="field" style={{ marginTop: 4 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder={offices ? 'Название, офис или город' : 'Название, стенд или город'} />
        </label>

        {shown.length === 0 ? (
          <div className="blankslate">
            <b>{list.length ? 'Никого не нашлось' : 'Поставщиков пока нет'}</b>
            {list.length ? 'Попробуйте другое слово.' : 'Заведите первого с визитки.'}
          </div>
        ) : (
          <div className="list">
            {shown.map((s) => (
              <button key={s.id} className="row" onClick={() => onOpenSupplier(s.id)}>
                <span className="mark">{(s.companyNameLocal ?? s.companyName).slice(0, 1)}</span>
                <span className="grow">
                  <span className="nm">{s.companyName}</span>
                  <span className="sub">
                    {[shows[s.exhibitionId], whereIs(s)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="num">{counts[s.id] ?? 0}</span>
                <span className="chev">›</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ещё ─────────────────────────────────────────────────────────────────────

export function More({ me, exhibition, onPickShow, onAccess, onSettings, onLogout, onTags, onCollections, onShop, onDesk, onTrash, onTalk, onTasks, onAssistant, logoutProblem }: {
  me: { name?: string; phone?: string; role?: string; workspace_name?: string } | null;
  exhibition: LocalExhibition | null;
  /** Есть только на широком экране: вернуться в кокпит. */
  onDesk?: () => void;
  onPickShow: () => void;
  onAccess: () => void;
  onSettings: () => void;
  onLogout: () => void;
  onTags: () => void;
  onCollections: () => void;
  onShop: () => void;
  onTrash: () => void;
  /** Беседа с поставщиком через переводчика. */
  onTalk: () => void;
  onTasks: () => void;
  /** Чат с ИИ: разобрать беседы, найти договорённости, вести задачи. */
  onAssistant: () => void;
  /** Почему выйти нельзя прямо сейчас — например, очередь ещё не отправлена. */
  logoutProblem?: string;
}) {
  const theme = useTheme();
  return (
    <div className="app tabbed">
      <div className="bar">
        <h1 className="t">Ещё</h1>
      </div>

      <div className="scroll">
        {me && (
          <div className="list" style={{ marginTop: 4 }}>
            <div className="row">
              <span className="grow">
                <span className="nm">{me.name}</span>
                <span className="sub">
                  {[me.phone && formatPhone(me.phone), me.workspace_name].filter(Boolean).join(' · ')}
                </span>
              </span>
            </div>
          </div>
        )}

        <div className="list" style={{ marginTop: 12 }}>
          <button className="row" onClick={onTalk}>
            <span className="grow">
              <span className="nm">Беседа с переводом</span>
              <span className="sub">Экран пополам: каждый читает собеседника на своём языке</span>
            </span>
            <span className="chev">›</span>
          </button>
          <button className="row" onClick={onTasks}>
            <span className="grow">
              <span className="nm">Задачи</span>
              <span className="sub">Договорённости из бесед и свои дела</span>
            </span>
            <span className="chev">›</span>
          </button>
          <button className="row" onClick={onAssistant}>
            <span className="grow">
              <span className="nm">Обработать беседы</span>
              <span className="sub">Чат с ИИ: цены, договорённости, задачи из разговоров</span>
            </span>
            <span className="chev">›</span>
          </button>
        </div>

        <div className="head"><h3>Настройки</h3></div>
        <div className="list">
          {onDesk && (
            <button className="row" onClick={onDesk}>
              <span className="grow">
                <span className="nm">Кокпит</span>
                <span className="sub">Широкий рабочий стол: заявки, витрина, разбор дня</span>
              </span>
              <span className="chev">›</span>
            </button>
          )}
          <button className="row" onClick={onPickShow}>
            <span className="grow">
              <span className="nm">{exhibition?.kind === 'building' ? 'Здание' : 'Выставка'}</span>
              <span className="sub">{exhibition ? placeTitle(exhibition) || exhibition.code : 'не выбрано'}</span>
            </span>
            <span className="chev">›</span>
          </button>

          {me?.role === 'owner' && (
            <button className="row" onClick={onAccess}>
              <span className="grow">
                <span className="nm">Кто может входить</span>
                <span className="sub">Добавить человека, закрыть вход по номеру</span>
              </span>
              <span className="chev">›</span>
            </button>
          )}

          <button className="row" onClick={onShop}>
            <span className="grow">
              <span className="nm">Магазин</span>
              <span className="sub">Витрина с каруселью, рендеры, заявки</span>
            </span>
            <span className="chev">›</span>
          </button>
          <button className="row" onClick={onCollections}>
            <span className="grow">
              <span className="nm">Подборки покупателям</span>
              <span className="sub">Витрины камней по ссылке, без цен</span>
            </span>
            <span className="chev">›</span>
          </button>
          <button className="row" onClick={onTags}>
            <span className="grow">
              <span className="nm">Виды камня</span>
              <span className="sub">Теги, которыми помечаете кадры</span>
            </span>
            <span className="chev">›</span>
          </button>
          <button className="row" onClick={onTrash}>
            <span className="grow">
              <span className="nm">Корзина</span>
              <span className="sub">Убранное хранится неделю — можно вернуть</span>
            </span>
            <span className="chev">›</span>
          </button>
          <button className="row" onClick={() => void toggleTheme()}>
            <span className="grow">
              <span className="nm">Оформление</span>
              <span className="sub">{THEME_NAME[theme]} · сменить на «{THEME_NAME[nextTheme(theme)]}»</span>
            </span>
            <span className="chev">⇄</span>
          </button>
          <button className="row" onClick={onSettings}>
            <span className="grow">
              <span className="nm">Это устройство</span>
              <span className="sub">Очередь, занятое место, ключ</span>
            </span>
            <span className="chev">›</span>
          </button>
        </div>

        {logoutProblem && <div className="memo warn">{logoutProblem}</div>}
        <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={onLogout}>
          Выйти
        </button>
        <p className="meta" style={{ marginTop: 8 }}>
          Выход стирает каталог с этого телефона — на сервере он остаётся.
        </p>
      </div>
    </div>
  );
}
